/**
 * SKILL.md 加载器（D-57）。把技能从"内置表"升级为"文件加载 + 清单 + 许可元数据"的可扩展库。
 *
 * 约定（对齐 Agent Skills 标准）：`public/skills/manifest.json` 列条目，每条指向一个 `SKILL.md`；
 * SKILL.md = YAML frontmatter（name/genre/blurb/style/license/attribution）+ 正文（创作提示词）。
 * 运行时 fetch 加载；离线/失败回退到内置 SKILL_TEMPLATES。第三方技能须按 license 合规引入（见跨会话记忆）。
 * parseSkillMd/mergeSkills 为纯函数、可测；loadSkills 触网（测试环境无 fetch → 回退）。
 */
import { SKILL_TEMPLATES, type SkillTemplate } from './skills';

export interface SkillManifestEntry {
  id: string;
  name: string;
  genre: string;
  /** SKILL.md 相对路径，如 /skills/xxx/SKILL.md */
  file: string;
  license?: string | undefined;
  attribution?: string | undefined;
}

export interface ParsedSkillMd {
  meta: Record<string, string>;
  body: string;
}

/** 解析 SKILL.md：提取 YAML frontmatter（key: value 逐行）与正文。无 frontmatter 则整体作正文。 */
export function parseSkillMd(md: string): ParsedSkillMd {
  const m = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/.exec(md.trim());
  if (m === null) {
    return { meta: {}, body: md.trim() };
  }
  const meta: Record<string, string> = {};
  for (const line of (m[1] ?? '').split(/\r?\n/)) {
    const i = line.indexOf(':');
    if (i > 0) {
      meta[line.slice(0, i).trim()] = line.slice(i + 1).trim();
    }
  }
  return { meta, body: (m[2] ?? '').trim() };
}

/** 合并技能：以 id 去重，外部（文件）优先覆盖内置同名。 */
export function mergeSkills(builtin: SkillTemplate[], external: SkillTemplate[]): SkillTemplate[] {
  const byId = new Map<string, SkillTemplate>();
  for (const s of builtin) {
    byId.set(s.id, s);
  }
  for (const s of external) {
    byId.set(s.id, s);
  }
  return [...byId.values()];
}

/** 从 manifest + SKILL.md 加载外部技能；失败/无 fetch 返回空数组（调用方回退内置）。 */
async function fetchExternalSkills(): Promise<SkillTemplate[]> {
  if (typeof fetch !== 'function' || typeof window === 'undefined') {
    return [];
  }
  try {
    const res = await fetch('/skills/manifest.json');
    if (!res.ok) {
      return [];
    }
    const list = (await res.json()) as SkillManifestEntry[];
    if (!Array.isArray(list)) {
      return [];
    }
    const out: SkillTemplate[] = [];
    for (const e of list) {
      try {
        const r = await fetch(e.file);
        if (!r.ok) {
          continue;
        }
        const { meta, body } = parseSkillMd(await r.text());
        out.push({
          id: e.id,
          name: meta.name ?? e.name,
          genre: meta.genre ?? e.genre,
          blurb: meta.blurb ?? '',
          scriptPrompt: body,
          styleId: meta.style,
        });
      } catch {
        /* 单个技能加载失败跳过 */
      }
    }
    return out;
  } catch {
    return [];
  }
}

/** 加载全部技能：内置 + 外部文件（外部同名覆盖），异常时至少返回内置。 */
export async function loadSkills(): Promise<SkillTemplate[]> {
  const external = await fetchExternalSkills();
  return mergeSkills(SKILL_TEMPLATES, external);
}
